/**
 * Biometric Authentication for InvestMenter
 * 
 * Supports TWO modes:
 * 1. PWA Mode: Uses WebAuthn API for browser-based biometric auth
 * 2. Native Mode: Uses Capacitor Native Biometric plugin
 * 
 * Automatically detects which mode to use based on environment.
 */

// Check if running in Capacitor native environment
const isNative = (): boolean => {
  return typeof (window as any).Capacitor !== 'undefined' && 
         (window as any).Capacitor.isNativePlatform();
};

// Server identifier
const SERVER = 'com.investmenter.app';

// Dynamic import for Capacitor (only loads in native mode)
let NativeBiometric: any = null;

const loadCapacitorBiometric = async () => {
  if (isNative() && !NativeBiometric) {
    try {
      const module = await import('capacitor-native-biometric');
      NativeBiometric = module.NativeBiometric;
      return true;
    } catch (e) {
      console.log('Capacitor biometric not available');
      return false;
    }
  }
  return !!NativeBiometric;
};

// ==================== RESULT INTERFACES ====================

interface BiometricResult {
  success: boolean;
  error?: string;
  biometryType?: string;
}

interface CredentialsResult {
  success: boolean;
  username?: string;
  password?: string;
  error?: string;
}

// ==================== WEBAUTHN (PWA) FUNCTIONS ====================

// Convert string to ArrayBuffer
const stringToBuffer = (str: string): ArrayBuffer => {
  return new TextEncoder().encode(str).buffer;
};

// Convert ArrayBuffer to base64
const bufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

// Convert base64 to ArrayBuffer
const base64ToBuffer = (base64: string): ArrayBuffer => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// Check if WebAuthn is available
const isWebAuthnAvailable = (): boolean => {
  return typeof window !== 'undefined' &&
         typeof window.PublicKeyCredential !== 'undefined' && 
         typeof navigator.credentials !== 'undefined' &&
         typeof navigator.credentials.create === 'function' && 
         typeof navigator.credentials.get === 'function';
};

// Check if platform authenticator (Face ID / Fingerprint) is available
const checkWebAuthnBiometric = async (): Promise<BiometricResult> => {
  if (!isWebAuthnAvailable()) {
    return { success: false, error: 'WebAuthn is not supported in this browser' };
  }

  try {
    const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    if (available) {
      return { success: true, biometryType: 'Biometric (Face/Fingerprint)' };
    }
    return { success: false, error: 'No biometric authenticator available on this device' };
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to check biometric availability' };
  }
};

// Enroll biometric using WebAuthn
// password param kept for API compatibility with native Capacitor path; not stored in PWA mode
const enrollWebAuthn = async (email: string, _password: string): Promise<BiometricResult> => {
  try {
    // Generate a random challenge
    const challenge = new Uint8Array(32);
    crypto.getRandomValues(challenge);

    // Create credential options
    const createOptions: CredentialCreationOptions = {
      publicKey: {
        challenge: challenge,
        rp: {
          name: 'Asset Desk',
          id: window.location.hostname,
        },
        user: {
          id: stringToBuffer(email),
          name: email,
          displayName: email.split('@')[0],
        },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },   // ES256
          { type: 'public-key', alg: -257 }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform', // Use built-in biometric
          userVerification: 'required',
          residentKey: 'required',
        },
        timeout: 60000,
      },
    };

    // Create the credential
    const credential = await navigator.credentials.create(createOptions) as PublicKeyCredential;
    
    if (!credential) {
      return { success: false, error: 'Failed to create credential' };
    }

    // Store only the credential ID — never the password
    const credentialId = bufferToBase64(credential.rawId);
    localStorage.setItem(`webauthn_credential_${email}`, credentialId);
    localStorage.setItem('webauthn_last_email', email);

    return { success: true, biometryType: 'Biometric (Face/Fingerprint)' };
  } catch (error: any) {
    console.error('WebAuthn enrollment error:', error);
    
    if (error.name === 'NotAllowedError') {
      return { success: false, error: 'Biometric enrollment was cancelled or denied' };
    }
    if (error.name === 'InvalidStateError') {
      return { success: false, error: 'Biometric is already enrolled for this account' };
    }
    
    return { success: false, error: error.message || 'Failed to enroll biometric' };
  }
};

// Authenticate using WebAuthn
const authenticateWebAuthn = async (): Promise<CredentialsResult> => {
  try {
    const email = localStorage.getItem('webauthn_last_email');
    if (!email) {
      return { success: false, error: 'No biometric login configured. Please login with password first.' };
    }

    const storedCredentialId = localStorage.getItem(`webauthn_credential_${email}`);
    if (!storedCredentialId) {
      return { success: false, error: 'Biometric not set up for this account' };
    }

    // Generate challenge
    const challenge = new Uint8Array(32);
    crypto.getRandomValues(challenge);

    // Get credential options
    const getOptions: CredentialRequestOptions = {
      publicKey: {
        challenge: challenge,
        rpId: window.location.hostname,
        allowCredentials: [{
          type: 'public-key',
          id: base64ToBuffer(storedCredentialId),
          transports: ['internal'],
        }],
        userVerification: 'required',
        timeout: 60000,
      },
    };

    // Get the credential (this prompts for biometric)
    const credential = await navigator.credentials.get(getOptions) as PublicKeyCredential;
    
    if (!credential) {
      return { success: false, error: 'Authentication failed' };
    }

    // Biometric proof verified — return identity only (no password)
    return {
      success: true,
      username: email,
    };
  } catch (error: any) {
    console.error('WebAuthn authentication error:', error);
    
    if (error.name === 'NotAllowedError') {
      return { success: false, error: 'Authentication was cancelled or denied' };
    }
    
    return { success: false, error: error.message || 'Biometric authentication failed' };
  }
};

// Check if WebAuthn credentials exist
const hasWebAuthnCredentials = (): boolean => {
  const email = localStorage.getItem('webauthn_last_email');
  if (!email) return false;
  return !!localStorage.getItem(`webauthn_credential_${email}`);
};

// Remove WebAuthn credentials
const removeWebAuthnCredentials = (email: string): void => {
  localStorage.removeItem(`webauthn_credential_${email}`);
  if (localStorage.getItem('webauthn_last_email') === email) {
    localStorage.removeItem('webauthn_last_email');
  }
};

// ==================== CAPACITOR (NATIVE) FUNCTIONS ====================

const checkCapacitorBiometric = async (): Promise<BiometricResult> => {
  await loadCapacitorBiometric();
  
  if (!NativeBiometric) {
    return { success: false, error: 'Native biometric not available' };
  }

  try {
    const result = await NativeBiometric.isAvailable();
    
    let biometryType = 'Biometric';
    if (result.biometryType === 1) biometryType = 'Face ID';
    else if (result.biometryType === 2) biometryType = 'Touch ID';
    else if (result.biometryType === 3) biometryType = 'Fingerprint';
    else if (result.biometryType === 4) biometryType = 'Face Authentication';
    
    return {
      success: result.isAvailable,
      biometryType,
    };
  } catch (error: any) {
    return { success: false, error: error.message || 'Biometric not available' };
  }
};

const enrollCapacitorBiometric = async (email: string, password: string): Promise<BiometricResult> => {
  await loadCapacitorBiometric();
  
  if (!NativeBiometric) {
    return { success: false, error: 'Native biometric not available' };
  }

  try {
    // Verify identity first
    await NativeBiometric.verifyIdentity({
      reason: 'Enable biometric login for Asset Desk',
      title: 'Enable Biometric Login',
      subtitle: 'Use your face or fingerprint to login',
    });

    // Store credentials
    await NativeBiometric.setCredentials({
      username: email,
      password: password,
      server: SERVER,
    });

    return { success: true, biometryType: 'Biometric' };
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to enable biometric' };
  }
};

const authenticateCapacitorBiometric = async (): Promise<CredentialsResult> => {
  await loadCapacitorBiometric();
  
  if (!NativeBiometric) {
    return { success: false, error: 'Native biometric not available' };
  }

  try {
    // Verify identity
    await NativeBiometric.verifyIdentity({
      reason: 'Login to Asset Desk',
      title: 'Biometric Login',
    });

    // Get credentials
    const credentials = await NativeBiometric.getCredentials({ server: SERVER });

    return {
      success: true,
      username: credentials.username,
      password: credentials.password,
    };
  } catch (error: any) {
    if (error.message?.includes('User cancelled')) {
      return { success: false, error: 'Authentication cancelled' };
    }
    return { success: false, error: error.message || 'Authentication failed' };
  }
};

const hasCapacitorCredentials = async (): Promise<boolean> => {
  await loadCapacitorBiometric();
  
  if (!NativeBiometric) return false;

  try {
    const credentials = await NativeBiometric.getCredentials({ server: SERVER });
    return !!credentials.username;
  } catch {
    return false;
  }
};

const removeCapacitorCredentials = async (): Promise<void> => {
  await loadCapacitorBiometric();
  
  if (NativeBiometric) {
    try {
      await NativeBiometric.deleteCredentials({ server: SERVER });
    } catch (e) {
      console.log('No credentials to delete');
    }
  }
};

// ==================== UNIFIED API (AUTO-DETECTS MODE) ====================

/**
 * Check if biometric authentication is available
 */
export const checkBiometricAvailability = async (): Promise<BiometricResult> => {
  if (isNative()) {
    console.log('🔐 Using Native Biometric (Capacitor)');
    return checkCapacitorBiometric();
  } else {
    console.log('🔐 Using WebAuthn (PWA)');
    return checkWebAuthnBiometric();
  }
};

/**
 * Enroll biometric authentication
 */
export const enrollBiometric = async (email: string, password: string): Promise<BiometricResult> => {
  if (isNative()) {
    return enrollCapacitorBiometric(email, password);
  } else {
    return enrollWebAuthn(email, password);
  }
};

/**
 * Authenticate with biometric
 */
export const authenticateWithBiometric = async (): Promise<CredentialsResult> => {
  if (isNative()) {
    return authenticateCapacitorBiometric();
  } else {
    return authenticateWebAuthn();
  }
};

/**
 * Check if biometric credentials exist
 */
export const hasBiometricCredentials = async (): Promise<boolean> => {
  if (isNative()) {
    return hasCapacitorCredentials();
  } else {
    return hasWebAuthnCredentials();
  }
};

/**
 * Disable biometric authentication
 */
export const disableBiometric = async (email?: string): Promise<BiometricResult> => {
  try {
    if (isNative()) {
      await removeCapacitorCredentials();
    } else if (email) {
      removeWebAuthnCredentials(email);
    } else {
      const storedEmail = localStorage.getItem('webauthn_last_email');
      if (storedEmail) removeWebAuthnCredentials(storedEmail);
    }
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to disable biometric' };
  }
};

/**
 * Get the current authentication mode
 */
export const getBiometricMode = (): 'native' | 'webauthn' => {
  return isNative() ? 'native' : 'webauthn';
};

/** True inside the installed iOS / Android app (Capacitor), false in a browser. */
export const isNativeApp = (): boolean => isNative();

/**
 * After a successful PASSWORD sign-in: if Face ID is already set up for this
 * same account, refresh the saved password silently — otherwise a password
 * change would leave Face ID signing in with the old one and failing.
 * Native only; the browser (WebAuthn) path never stores a password.
 */
export const refreshBiometricPassword = async (email: string, password: string): Promise<void> => {
  if (!isNative()) return;
  await loadCapacitorBiometric();
  if (!NativeBiometric) return;
  try {
    const saved = await NativeBiometric.getCredentials({ server: SERVER });
    if (saved?.username && saved.username.toLowerCase() === email.toLowerCase() && saved.password !== password) {
      await NativeBiometric.setCredentials({ username: email, password, server: SERVER });
    }
  } catch {
    /* nothing saved — nothing to refresh */
  }
};

// ---- Bank-style auto prompt -------------------------------------------------
// The sign-in screen offers Face ID by itself when the app opens. Not straight
// after the user deliberately signed out, though, or Face ID would sign them
// back in before they could switch account. sessionStorage: a fresh app launch
// starts clean, so the prompt comes back on the next open.
const SKIP_AUTO_KEY = 'faceid_skip_auto';

export const markSignedOut = (): void => {
  try { sessionStorage.setItem(SKIP_AUTO_KEY, '1'); } catch { /* ignore */ }
};

export const shouldAutoPromptFaceId = (): boolean => {
  try { return sessionStorage.getItem(SKIP_AUTO_KEY) !== '1'; } catch { return true; }
};

export default {
  checkBiometricAvailability,
  enrollBiometric,
  authenticateWithBiometric,
  hasBiometricCredentials,
  disableBiometric,
  getBiometricMode,
  isNativeApp,
  refreshBiometricPassword,
  markSignedOut,
  shouldAutoPromptFaceId,
};